import React, { useState, useEffect, useRef } from "react";
import OverviewTab from "./OverviewTab";
import axios from "axios";
import { Link } from "react-router-dom";

// Custom dropdown component with click outside support
const TimeFilterDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef();
  const options = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last7days" },
    { label: "Last Week", value: "lastweek" },
    { label: "Last Month", value: "lastmonth" },
    { label: "All Time", value: "alltime" },
  ];
  useEffect(() => {
    function check(e) {
      if (open && containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", check);
    return () => document.removeEventListener("mousedown", check);
  }, [open]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div className="relative w-full sm:w-auto z-10" ref={containerRef}>
      <button
        className="w-full h-9 px-3 py-2 bg-white border-2 rounded-lg text-[12px] font-semibold flex items-center justify-between transition-all duration-200"
        onClick={() => setOpen((prev) => !prev)}
        type="button"
      >
        <span>
          {options.find((opt) => opt.value === value)?.label || "Select Time"}
        </span>
        <svg
          className={`ml-2 w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        className={`absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 overflow-hidden transition-all duration-300 ${
          open ? "max-h-70 opacity-100 scale-100" : "max-h-0 opacity-0 scale-95"
        }`}
        style={{ pointerEvents: open ? "auto" : "none" }}
      >
        {options.map((opt) => (
          <div
            key={opt.value}
            className={`px-4 py-2 text-[12px] cursor-pointer hover:bg-[#0CBB7D] hover:text-white transition-colors duration-200 ${
              value === opt.value ? "bg-[#e8f6f2] text-[#0CBB7D]" : "text-gray-700"
            }`}
            onClick={() => handleSelect(opt.value)}
          >
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [getuser, setGetuser] = useState({});
  const [kycCompleted, setKycCompleted] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const hasFetched = useRef(false);
  const [searchUser, setSearchUser] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminTab, setAdminTab] = useState(false);

  // Time filter state
  const [timeFilter, setTimeFilter] = useState("today");

  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchUserData = async () => {
    try {
      const [session, token] = document.cookie.split("=");
      if (!token) return;

      const userResponse = await axios.get(
        `${REACT_APP_BACKEND_URL}/order/getUser`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsAdmin(userResponse.data.isAdmin);
      setAdminTab(userResponse.data.adminTab);
      setGetuser(userResponse.data);
      setKycCompleted(userResponse.data.kycDone);
    } catch (userError) {}
  };

  useEffect(() => {
    const kyc = localStorage.getItem("kyc");
    setKycCompleted(kyc === "true");
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchUserData();
  }, []);

  useEffect(() => {
    if (
      searchUser.trim().length > 2 &&
      userSuggestions.length === 1 &&
      `${userSuggestions[0].fullname} (${userSuggestions[0].email})` === searchUser &&
      selectedUserId !== userSuggestions[0]._id
    ) {
      setSelectedUserId(userSuggestions[0]._id);
    }
  }, [searchUser, userSuggestions]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (searchUser.trim().length < 2) return setUserSuggestions([]);
      try {
        const res = await axios.get(
          `${REACT_APP_BACKEND_URL}/admin/searchUser?query=${searchUser}`
        );
        setUserSuggestions(res.data.users);
      } catch (err) {}
    };
    const debounce = setTimeout(fetchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchUser]);

  return (
    <div className="sm:px-2 p-1">
      {!kycCompleted && (
        <div className="flex flex-col items-center border border-dashed border-red-500 rounded-lg bg-red-50 px-3 py-2 mb-6 shadow-md w-full max-w-[100%] sm:max-w-full sm:flex-row sm:items-center sm:justify-between">
          <span className="text-yellow-400 text-2xl sm:mr-3">💡</span>
          <p className="text-[12px] text-gray-900 font-medium text-center sm:text-left mt-2 sm:mt-0 whitespace-nowrap">
            Complete your KYC to Start Shipping
          </p>
          <Link
            to="/Kyc"
            className="mt-3 sm:mt-0 sm:ml-auto text-red-500 text-sm font-medium hover:underline"
          >
            Click Here ›
          </Link>
        </div>
      )}

      {/* Filter Row */}
      <div className="w-full flex flex-row sm:items-center sm:justify-between gap-2 mb-2">
        {isAdmin && adminTab ? (
          // ✅ Admin: search input near date filter (right side on desktop)
          <div className="flex w-full sm:justify-end items-center gap-2 sm:gap-3">
            <div className="w-[120px] sm:w-[110px] text-gray-400">
              <TimeFilterDropdown value={timeFilter} onChange={setTimeFilter} />
            </div>
            <div className="relative w-full sm:w-[280px]">
              <input
                type="text"
                placeholder="Search by Name, Email, or Contact"
                className="w-full h-9 py-2 px-3 font-[600] placeholder:text-[12px] text-[12px] border-2 rounded-lg placeholder:text-gray-400 focus:outline-none"
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchUser(value);
                  if (value.trim() === "") {
                    setSelectedUserId(null);
                  }
                }}
                value={searchUser}
              />
              {userSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full bg-white shadow-lg rounded-md mt-1 z-40 max-h-60 overflow-y-auto">
                  {userSuggestions.map((user, index) => (
                    <div
                      key={user._id}
                      className={`flex cursor-pointer group transition-colors duration-300 ${
                        index !== userSuggestions.length
                          ? "border-b border-gray-200 hover:bg-gray-100"
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedUserId(user._id);
                        setSearchUser(`${user.fullname} (${user.email})`);
                        setUserSuggestions([]);
                      }}
                    >
                      <div className="w-1/4 flex items-center justify-center p-2">
                        <p className="text-[12px] text-gray-400 group-hover:text-[#0CBB7D] font-medium truncate text-center">
                          {user.userId}
                        </p>
                      </div>
                      <div className="w-3/4 flex flex-col justify-center py-[7px] pr-2 leading-tight">
                        <p className="text-[13px] text-gray-500 group-hover:text-[#0CBB7D] font-medium truncate">
                          {user.fullname}
                        </p>
                        <p className="text-[11px] text-gray-400 truncate">{user.email}</p>
                        <p className="text-[11px] text-gray-400 truncate">{user.phoneNumber}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          // ✅ User: only date filter (right side, not full width)
          <div className="flex w-full justify-end">
            <div className="w-[100px] sm:w-[110px] text-gray-400">
              <TimeFilterDropdown value={timeFilter} onChange={setTimeFilter} />
            </div>
          </div>
        )}
      </div>

      {/* Only show the overview tab/dashboard */}
      <OverviewTab
        refresh={refresh}
        selectedUserId={selectedUserId}
        timeFilter={timeFilter}
      />
    </div>
  );
};

export default Dashboard;
